import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import Index from "./pages/Index";
import Login from "./pages/Login";
import UserManagement from "./pages/UserManagement";
import Contacts from "./pages/Contacts";
import ContactAccessLogs from "./pages/ContactAccessLogs";
import ContactAssignments from "./pages/ContactAssignments";
import Logs from "./pages/Logs";
import FormValidation from "./pages/FormValidation";
import EmailForms from "./pages/EmailForms";
import FormSubmissions from "./pages/FormSubmissions";
import AIPlayground from "./pages/AIPlayground";
import Profile from "./pages/Profile";
import AIKnowledge from "./pages/AIKnowledge";
import AIAssistantProfile from "./pages/AIAssistantProfile";
import LLMConfig from "./pages/LLMConfig";
import ContactMigration from "./pages/ContactMigration";
import EntityTypeManagement from "./pages/EntityTypeManagement";
import NotFound from "./pages/NotFound";
import { RoleProtectedRoute } from "./components/RoleProtectedRoute";
import { useUserRole } from "./hooks/useUserRole";

const queryClient = new QueryClient();

// Component to redirect RSVP users from home to form submissions
const RsvpRedirect = ({ children }: { children: React.ReactNode }) => {
  const { isRsvp, loading } = useUserRole();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isRsvp) {
    return <Navigate to="/form-submissions" replace />;
  }

  return <>{children}</>;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log("🔐 [AUTH] Verificando autenticação...");
    
    // Timeout para detectar carregamento lento
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn("⚠️ [AUTH] Timeout de 10s - ainda verificando autenticação");
        setError("A verificação de autenticação está demorando mais que o esperado");
      }
    }, 10000);

    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error("❌ [AUTH] Erro ao buscar sessão:", error);
          setError(error.message);
          setLoading(false);
          return;
        }
        console.log("✅ [AUTH] Sessão carregada:", session ? "Autenticado" : "Não autenticado");
        setSession(session);
        setLoading(false);
      })
      .catch((err) => {
        console.error("❌ [AUTH] Erro crítico ao buscar sessão:", err);
        setError("Erro ao verificar autenticação");
        setLoading(false);
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("🔄 [AUTH] Mudança de autenticação:", _event, session ? "Autenticado" : "Não autenticado");
      setSession(session);
      setLoading(false);
    });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="text-sm text-muted-foreground">Verificando autenticação...</p>
        {error && (
          <p className="text-sm text-destructive max-w-md text-center">{error}</p>
        )}
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-destructive">Erro ao verificar autenticação</p>
        <button 
          onClick={() => window.location.href = '/login'}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
        >
          Ir para Login
        </button>
      </div>
    );
  }

  if (!session) {
    console.log("🔒 [AUTH] Redirecionando para login");
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <RsvpRedirect>
                  <RoleProtectedRoute allowedRoles={['admin', 'editor', 'cliente']}>
                    <Index />
                  </RoleProtectedRoute>
                </RsvpRedirect>
              </ProtectedRoute>
            } 
          />
          <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
          <Route 
            path="/contact-access-logs" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <ContactAccessLogs />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/contact-assignments" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <ContactAssignments />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route path="/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
          <Route path="/logs" element={<ProtectedRoute><Logs /></ProtectedRoute>} />
          <Route 
            path="/form-validation" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <FormValidation />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/email-forms" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin', 'editor']}>
                  <EmailForms />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/form-submissions" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin', 'editor', 'rsvp']}>
                  <FormSubmissions />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route path="/ai-playground" element={<ProtectedRoute><AIPlayground /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route 
            path="/profile/:userId" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <Profile />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ai-knowledge" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <AIKnowledge />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ai-assistant-profile" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <AIAssistantProfile />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/llm-config" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <LLMConfig />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/contact-migration" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <ContactMigration />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/entity-types" 
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin', 'editor']}>
                  <EntityTypeManagement />
                </RoleProtectedRoute>
              </ProtectedRoute>
            } 
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
